import React, { useState, useCallback, useEffect } from 'react';

/**
 * AktuellesFotos — displays post photos as thumbnails with a Lightbox.
 * Clicking a thumbnail opens the full-size image in a modal overlay.
 * Supports swipe (touch) and keyboard navigation.
 *
 * @param {{ photos: string[], publicBaseUrl?: string }} props
 */
const AktuellesFotos = ({ photos = [], publicBaseUrl = process.env.REACT_APP_GALLERY_PUBLIC_BASE_URL || 'https://kickereck-gallery-prod.s3.eu-central-1.amazonaws.com' }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const getPhotoUrl = (key) => {
        if (key.startsWith('http')) return key;
        const base = publicBaseUrl.replace(/\/$/, '');
        const cleanKey = key.startsWith('/') ? key : `/${key}`;
        return `${base}${cleanKey}`;
    };

    // The API returns thumb URLs (calendar-flyers-thumb/*.jpg) for fast loading.
    // In the lightbox we want full-resolution originals, so reverse the transformation.
    // Only applies to flyer thumb URLs — other photo URLs are returned unchanged.
    const toOriginalUrl = (url) => {
        if (typeof url !== 'string' || !/(\/calendar-flyers|\/flyers)-thumb\//i.test(url)) return url;
        return url
            .replace(/(\/calendar-flyers|\/flyers)-thumb\//, '$1/')
            .replace(/\.jpg(\?|$)/i, '.png$1');
    };

    const openLightbox = (index) => {
        setCurrentIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => setLightboxOpen(false);

    const goNext = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % photos.length);
    }, [photos.length]);

    const goPrev = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
    }, [photos.length]);

    useEffect(() => {
        if (!lightboxOpen) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft') goPrev();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [lightboxOpen, goNext, goPrev]);

    if (!photos || photos.length === 0) {
        return null;
    }

    return (
        <div className="aktuelles-fotos" data-testid="aktuelles-fotos">
            <div className="row" style={{ marginTop: '12px' }}>
                {photos.map((photo, index) => (
                    <div key={index} className="col-xs-4 col-sm-3 col-md-2">
                        <img
                            src={getPhotoUrl(photo)}
                            alt={`Foto ${index + 1}`}
                            className="img-thumbnail"
                            style={{ cursor: 'pointer', width: '100%', height: '100px', objectFit: 'cover' }}
                            onClick={() => openLightbox(index)}
                            data-testid={`aktuelles-thumb-${index}`}
                        />
                    </div>
                ))}
            </div>

            {lightboxOpen && (
                <div
                    className="aktuelles-lightbox-overlay"
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                    onClick={closeLightbox}
                    data-testid="aktuelles-lightbox"
                >
                    <button
                        style={{
                            position: 'absolute', top: '20px', right: '20px',
                            color: 'white', background: 'none', border: 'none',
                            fontSize: '30px', cursor: 'pointer', zIndex: 1
                        }}
                        onClick={closeLightbox}
                    >
                        &times;
                    </button>

                    {photos.length > 1 && (
                        <button
                            style={{
                                position: 'absolute', left: '20px', top: '50%',
                                color: 'white', background: 'rgba(0,0,0,0.5)', border: 'none',
                                fontSize: '40px', cursor: 'pointer', padding: '10px', zIndex: 1
                            }}
                            onClick={(e) => { e.stopPropagation(); goPrev(); }}
                        >
                            &#8249;
                        </button>
                    )}

                    <img
                        src={getPhotoUrl(toOriginalUrl(photos[currentIndex]))}
                        alt={`Foto ${currentIndex + 1}`}
                        style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="aktuelles-lightbox-image"
                    />

                    {photos.length > 1 && (
                        <button
                            style={{
                                position: 'absolute', right: '20px', top: '50%',
                                color: 'white', background: 'rgba(0,0,0,0.5)', border: 'none',
                                fontSize: '40px', cursor: 'pointer', padding: '10px', zIndex: 1
                            }}
                            onClick={(e) => { e.stopPropagation(); goNext(); }}
                        >
                            &#8250;
                        </button>
                    )}

                    {photos.length > 1 && (
                        <div style={{
                            position: 'absolute', bottom: '20px', color: 'white',
                            fontSize: '14px'
                        }}>
                            {currentIndex + 1} / {photos.length}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AktuellesFotos;
